/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-one-expression-per-line */
import styled from 'styled-components';
import PropTypes from 'prop-types';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import PriceHike from '@comp/PriceHike/PriceHike';
import { useSelector, useDispatch } from 'react-redux';
import H2 from '@base/H2/H2';
import H3 from '@base/H3/H3';
import H4 from '@base/H4/H4';
import H5 from '@base/H5/H5';
import Divider from '@base/Divider/Divider';
import textHelper from '@utils/textHelper';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import analyticsAction from '@actions/analytics';
import { TextColors, Spacings, Fonts } from '@styles/index';
import imageHelper from '@utils/imageHelper';
import useWindowSize from '@hooks/useWindowSize';
import TriangleUp from './TriangleUp';
import MobileIconicSection from './Iconic/MobileIconicSection';

const data = [
  {
    name: 'Daily Live classes',
    description:
      'Chat with your educator, engage in discussions, ask your doubts, and answer polls - all while the class is going on',
    image: 'goal/benefitchat.svg'
  },
  {
    name: 'Live tests & quizzes',
    description:
      'Evaluate your preparation with our regular mock tests and quizzes and get detailed analysis on your performance',
    image: 'goal/benefittest.svg'
  },
  {
    name: 'Structured courses',
    description:
      'All our courses are structured in line with your exam syllabus to help you best prepare for it',
    image: 'goal/benefitcourses.svg'
  },
  {
    name: 'Unlimited access',
    description:
      'One subscription gets you access to all our live and recorded courses to watch from the comfort of any of your devices',
    image: 'goal/benefitaccess.svg'
  }
];

const SecWrapper = styled.section`
  width: 100%;
  position: relative;
`;

const Container = styled.div`
  background-color: #fff;
  box-shadow: ${({ theme }) => boxShadowGenerator({ theme })};
  border-radius: ${Spacings.SPACING_2B};
  ${({ hasIconicSection }) =>
    !hasIconicSection &&
    `top: -${Spacings.SPACING_10B}; margin-bottom: -${Spacings.SPACING_10B};`}
  position: relative;
  margin: 0 auto;
  text-align: center;
  width: 1136px;
  padding-top: ${Spacings.SPACING_20B};
  @media (max-width: 70.95em) {
    width: 728px;
    padding-top: 32px;
  }
`;

const Wrapper = styled.div`
  margin-top: 16px;
  margin-bottom: -32px;
`;

const CardsContainer = styled.div`
  display: flex;
  margin-left: ${Spacings.SPACING_24B};
  margin-right: ${Spacings.SPACING_24B};
  margin-top: 74px;
  flex-wrap: wrap;
  @media (min-width: 70.95em) {
    & > :nth-child(even) {
      margin-left: ${Spacings.SPACING_24B};
    }
  }
  @media (max-width: 70.95em) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 78px;
    grid-row-gap: 32px;
    margin: 40px 58px 32px 61px;
  }
`;

const CardWrapper = styled.div`
  width: 416px;
  margin-bottom: 66px;
  text-align: left;
  display: flex;
  @media (max-width: 70.95em) {
    width: 100%;
    margin-bottom: 0px;
  }
`;

const CardContentLeft = styled.div`
  margin-right: ${Spacings.SPACING_4B};
`;

const CardContentRight = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Description = styled(H5)`
  font-weight: ${Fonts.REGULAR};
  margin-top: ${Spacings.SPACING_4B};
  @media (max-width: 70.95em) {
    font-size: 14px;
    margin-top: 8px;
  }
`;

const Image = styled.img`
  user-drag: none;
  user-select: none;
  @media (max-width: 70.95em) {
    width: 48px;
    height: 48px;
    object-fit: contain;
  }
`;

const SubHeading = styled(H5)`
  font-weight: ${Fonts.REGULAR};
  margin-top: ${Spacings.SPACING_2B};
`;

const CardTitle = styled(H3)`
  @media (max-width: 70.95em) {
    font-size: 16px;
    line-height: 150%;
  }
`;

const Card = ({ card }) => (
  <CardWrapper>
    <CardContentLeft>
      <Image src={imageHelper.getImage(card.image)} alt={card.name} />
    </CardContentLeft>
    <CardContentRight>
      <CardTitle>{card.name}</CardTitle>
      <Description>{card.description}</Description>
    </CardContentRight>
  </CardWrapper>
);

const ExamPricing = styled(H3)`
  color: ${TextColors.BLUE};
  padding-left: 5px;
  @media (max-width: 70.95em) {
    font-size: 22px;
    display: inline-block;
  }
`;

const PrciningVal = styled.div`
  width: 600px;
  text-align: left;
  @media (max-width: 70.95em) {
    width: auto;
  }
`;

const Pricing = styled.div`
  padding: ${Spacings.SPACING_16B} ${Spacings.SPACING_24B};
  display: flex;
  justify-content: space-between;
  @media (max-width: 70.95em) {
    padding: 32px 58px 32px 61px;
  }
`;

const LinkContainer = styled.div`
  align-self: center;
`;

const UNDivider = styled(Divider)`
  margin: 0 ${Spacings.SPACING_24B};
  @media (max-width: 70.95em) {
    height: 2px;
    margin: 0 58px 0 61px;
  }
`;

const PricingTitle = styled(H3)`
  @media (max-width: 70.95em) {
    font-size: 18px;
    display: inline-block;
  }
`;

const ViewPricing = styled(H4)`
  @media (max-width: 70.95em) {
    font-size: 16px;
    line-height: 100%;
  }
`;

const SecondSection = ({
  block,
  goalUID,
  goalSlug,
  goalDetails,
  countryCode,
  dataAnalytics,
  hasIconicSection
}) => {
  const { extraBlockinfo } = block;
  const { isNoTestGoal } = goalDetails;
  let staticData = data;
  if (isNoTestGoal && block?.data && block?.data.length === 4) {
    staticData = block.data.map((item, i) => {
      return {
        ...item,
        image: data[i].image
      };
    });
  }
  const { isMobile } = useWindowSize();
  const dispatch = useDispatch();
  const goalCurrencyCode = textHelper.currencySymbolMap(countryCode);
  const handleAnalytics = () => {
    dispatch(
      analyticsAction(
        `${dataAnalytics}-viewPricing`,
        goalUID,
        {},
        { 'Last Primary Source': 'goal page' }
      )
    );
  };

  const partPaymentStatus = useSelector(
    ({ checkout }) => checkout.partPaymentStatus?.[goalUID]
  );

  const { priceHikeInfoArr } = useSelector(({ others }) => ({
    priceHikeInfoArr: others.priceHikeInfo
  }));

  const priceHikeInfoIndex = priceHikeInfoArr.findIndex((obj) => {
    const priceHikeSupportedGoals = obj?.active_goals?.split(',');
    return obj?.is_active && priceHikeSupportedGoals?.includes(goalUID);
  });

  const priceHikeInfo = priceHikeInfoArr[priceHikeInfoIndex];

  const headerText = hasIconicSection ? 'PLUS subscription' : 'What you’ll get';
  const subHeading = 'Your access to the best of Unacademy';
  return isMobile ? (
    <Wrapper>
      <MobileIconicSection
        type="Plus"
        info={{ heading: 'PLUS', subHeading, data: staticData }}
      />
    </Wrapper>
  ) : (
    <SecWrapper
      showPriceHike={!!priceHikeInfo}
      hasPartPayment={partPaymentStatus?.is_active_part_set}
    >
      {!hasIconicSection && <TriangleUp />}
      <Container hasIconicSection={hasIconicSection}>
        <H2>{headerText}</H2>
        {hasIconicSection && <SubHeading>{subHeading}</SubHeading>}
        <CardsContainer>
          {staticData.map((card) => (
            <Card card={card} key={card.name} />
          ))}
        </CardsContainer>
        {!partPaymentStatus?.is_active_part_set && (
          <>
            <UNDivider />
            <Pricing>
              <PrciningVal>
                <PricingTitle>
                  {`${extraBlockinfo?.goal_name} ${
                    hasIconicSection ? '- PLUS ' : ''
                  }subscription starts `}
                  <br /> from
                  <ExamPricing>
                    {`${goalCurrencyCode}${textHelper.commas(
                      Math.round(extraBlockinfo?.lowest_price)
                    )}`}
                    /month
                  </ExamPricing>
                </PricingTitle>
              </PrciningVal>
              <LinkContainer>
                <UnderlineLink
                  color={TextColors.GREEN}
                  href="/goal/[goalSlug]/[goalUID]/subscribe"
                  as={`/goal/${goalSlug}/${goalUID}/subscribe`}
                  onClick={handleAnalytics}
                >
                  <ViewPricing
                    color={TextColors.GREEN}
                    data-analytics={`${dataAnalytics}-viewPricing`}
                  >
                    View pricing
                  </ViewPricing>
                </UnderlineLink>
              </LinkContainer>
            </Pricing>
          </>
        )}
        {priceHikeInfo && (
          <PriceHike
            iconURL={priceHikeInfo?.icon}
            message={priceHikeInfo?.price_hike_text}
            borderRadius={`0px 0px ${Spacings.SPACING_2B} ${Spacings.SPACING_2B}`}
          />
        )}
      </Container>
    </SecWrapper>
  );
};

SecondSection.propTypes = {
  block: PropTypes.objectOf(PropTypes.any).isRequired,
  goalUID: PropTypes.string.isRequired,
  goalSlug: PropTypes.string.isRequired,
  hasIconicSection: PropTypes.bool.isRequired,
  dataAnalytics: PropTypes.string
};

SecondSection.defaultProps = {
  dataAnalytics: ''
};

export default SecondSection;
